import { useEffect, useState } from "react"
import { Dialog } from "primereact/dialog"
import { useDispatch } from "react-redux"
import { v4 as uuidv4 } from 'uuid';

import Loading from "../../../common/Loading"

import { Button, DropDownField, TextAreaField, TextInputField } from "../../../helpers"

import { raiseToast } from "../../../services/redux/reducers/toast/toast"
import { getScrapStockByMaterialIdAndWarehouseId, reverseStockAjustment } from "../../../services/api/stockAdjustment"

import { scrapToSmartInitialValues, scrapToSmartValidationSchema } from "../../../validation/stockAdjustment"
import { validateInputs } from "../../../utils"


const ScrapToSmart = (props) => {

    const { setVisibleScrapToSmart, companyWarehouses, GSTRates } = props

    const [scrapToSmart, setScrapToSmart] = useState(scrapToSmartInitialValues)
    const [sellerPOItems, setSellerPOItems] = useState([])

    const [formErrors, setFormErrors] = useState([])
    
    const [isLoading, setIsLoading] = useState(false)

    const dispatch = useDispatch()

    useEffect(() => {
        setSellerPOItems([])
        setScrapToSmart({ ...scrapToSmart, remarks: null })
    }, [scrapToSmart.materialId, scrapToSmart.warehouseId])


    const handleScrapToSmartChange = (e) => {
        const updatedScrapToSmart = { ...scrapToSmart, [e.target.name]: e.target.value }
        setScrapToSmart({ ...updatedScrapToSmart })
    }
    
    const handleStockDetails = async () => {
        if (!scrapToSmart.materialId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter material ID!', life: 3000 }))
        }

        if (scrapToSmart.materialId.length <= 5) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'The length of material ID is at least 5!', life: 3000 }))
        }

        if (!scrapToSmart.warehouseId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter warehouse ID!', life: 3000 }))
        }

        setIsLoading(true)
        const scrapStockRes = await getScrapStockByMaterialIdAndWarehouseId({materialId: scrapToSmart.materialId, warehouseId: scrapToSmart.warehouseId})
        if (scrapStockRes.failure) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: "Scrap Stock", detail: scrapStockRes.data.error, life: 3000 }))
        }

        const updatedSellerPOItems = scrapStockRes.data.map(eachSellerPOItem => {
            return {...eachSellerPOItem, tempUUID: uuidv4()}
        })
        setSellerPOItems(updatedSellerPOItems)
        setIsLoading(false)
    }

    const handleSellerPOsChange = (id, name, value) => {
        const updatedSellerPOItems = sellerPOItems.map(eachSellerPOItem => {
            if(eachSellerPOItem.tempUUID === id){
                let updatedValue = value
                if (parseFloat(value) > parseFloat(eachSellerPOItem.moveQty)) {
                    dispatch(raiseToast({ severity: 'error', detail: `Max quantity available is ${parseFloat(eachSellerPOItem.moveQty)}`, life: 3000 }))
                    updatedValue = parseFloat(eachSellerPOItem.moveQty)
                }
                return {...eachSellerPOItem, [name]: updatedValue}
            }
            return eachSellerPOItem
        })

        setSellerPOItems([...updatedSellerPOItems])
    }


    const handleOnHideScrapToSmart = () => {
        setSellerPOItems([])
        setScrapToSmart(scrapToSmartInitialValues)
        setVisibleScrapToSmart(false)
    }

    const handleScrapToSmartSubmit = async () => {
        const validationRes = await validateInputs(scrapToSmartValidationSchema, scrapToSmart)
        if (validationRes.error) {
            setFormErrors(validationRes.error)
        }
        else {
            setFormErrors({})

            if (sellerPOItems.length === 0) {
                return dispatch(raiseToast({ severity: 'error', detail: `Please get stock details`, life: 3000 }))
            }

            const filteredSellerPOItems = sellerPOItems.filter(eachSellerPOItem => parseFloat(eachSellerPOItem.inputQty) > 0)

            if (filteredSellerPOItems.length === 0) {
                return dispatch(raiseToast({ severity: 'error', detail: `Please enter quantity to adjust!`, life: 3000 }))
            }

            const stockAdjustments = filteredSellerPOItems.map(eachSellerPOItem => {
                return {
                    materialId: parseInt(scrapToSmart.materialId),
                    warehouseId: scrapToSmart.warehouseId,
                    isPositiveAdjustment: false,
                    sellerPODocNo: eachSellerPOItem.docNo,
                    sellerPONo: eachSellerPOItem.docNo,
                    unitPrice: parseFloat(eachSellerPOItem.unitPrice),
                    GSTRate: eachSellerPOItem.GSTRate,
                    adjustedQty: parseFloat(eachSellerPOItem.inputQty),
                    remarks: scrapToSmart.remarks
                }
            })

            if (window.confirm("Are you sure you want to continue?")) {
                setIsLoading(true)
                const stockAdjustmentRes = await reverseStockAjustment({ stockAdjustments: stockAdjustments })

                if (stockAdjustmentRes.failure) {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'error', summary: 'Stock Adjustment', detail: stockAdjustmentRes.data.error, life: 3000 }))
                }
                setIsLoading(false)
                handleOnHideScrapToSmart()
            }
        }
    }

    return (
        <Dialog header="Scrap To Smart" visible={props.visible} style={{ minHeight: "60%", width: '60vw', }} onHide={handleOnHideScrapToSmart}>
            <Loading visible={isLoading} />
            <div className="flex flex-row">
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <TextInputField name="materialId" labelText="Material ID" value={scrapToSmart.materialId} onChange={handleScrapToSmartChange} errorMessage={formErrors.materialId} keyfilter={"pnum"} required />
                </div>
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <DropDownField name="warehouseId" labelText="Warehouse" options={companyWarehouses} value={scrapToSmart.warehouseId} optionLabel="warehouseName" optionValue="warehouseId" onChange={(e) => handleScrapToSmartChange(e)} errorMessage={formErrors.warehouseId} required />
                </div>
                <div className="flex align-items-center">
                    <Button label='Get Stock Details' className='h-2rem p-2' onClick={handleStockDetails} text />
                </div>
            </div>
            <div>
            {
                sellerPOItems.map((eachSelPOItem, index) => {
                    let displayLabel = false
                    if (index === 0) {
                        displayLabel = true
                    }

                    return (
                        <div className="flex flex-row" key={`sellerPOItems-${eachSelPOItem.tempUUID}`}>
                            <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                                <TextInputField name="sellerPODocNo" labelText={displayLabel && "Sel PO Doc No"} value={eachSelPOItem.docNo} keyfilter={"pnum"} disabled />
                            </div>
                            <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                <TextInputField name="price" labelText={displayLabel && "Price"} value={eachSelPOItem.price} keyfilter={"pnum"} disabled />
                            </div>
                            <div className="flex flex-column col-12 lg:col-1 lg:mb-0">
                                <DropDownField name="GSTRate" options={GSTRates} value={eachSelPOItem.GSTRate} labelText={displayLabel && "GST"} optionValue="GSTRate" optionLabel="GSTRateStr" disabled />
                            </div>
                            <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                <TextInputField name="moveQty" labelText={displayLabel && "Scrap Qty"} value={eachSelPOItem.moveQty} keyfilter={"pnum"} disabled />
                            </div>
                            <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                <TextInputField name="inputQty" labelText={displayLabel && "Input Qty"} value={eachSelPOItem.inputQty} keyfilter={"pnum"} onChange={(e) => {handleSellerPOsChange(eachSelPOItem.tempUUID, e.target.name, e.target.value)}} required />
                            </div>
                        </div>
                    )
                })
                }
                {
                    sellerPOItems.length ?
                    <>
                        <div>
                            <div className="flex flex-column col-12 text-area">
                                <TextAreaField name="remarks" labelText="Remarks" rows={3} onChange={handleScrapToSmartChange} value={scrapToSmart.remarks} errorMessage={formErrors.remarks} required maxLength={100} />
                            </div>
                        </div>
                        <div className='flex justify-content-center mt-2'>
                            <Button label="Submit" onClick={handleScrapToSmartSubmit} />
                        </div>
                    </> : null
                }
            </div>
        </Dialog>
    )
}

export default ScrapToSmart